import React from 'react';
import { Document } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { ReactSketchCanvasProps } from 'react-sketch-canvas';
import { Controller, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from '@punchcard/core';
import PageButton from '@punchcard/core/components/WorkSample/PageButton';
import FourOhFour from '@punchcard/core/components/404';
import PDFPageInteractive from './PDFPageInteractive';
import { usePDFReaderContext } from './PDFReaderContext';
import { isStudentApp } from '@punchcard/core/utils';

interface LinkState {
	tooltipPosition: { x: number; y: number };
	showTooltip: boolean;
	linkId: number | null;
	linkPage: number | null;
}

interface PDFViewportProps {
	pdfData: File | null;
	numPages?: number;
	onDocumentLoadSuccess: (proxy: PDFDocumentProxy) => void;
	options: object;
	pageNumberArray: number[];
	pageWidth: number;
	linkState: LinkState;
	handleInternalLinkClick?: (id: number, pageNumber: number) => void;
	hideLinkTooltip: () => void;
	onPageRenderSuccess: () => void;
	handleInternalLinkElement: (anchor: HTMLAnchorElement) => boolean;
	isActivityOpen: boolean;
	size: number;
	colour: string;
	eraserSize: number;
	addHistory: (index: number) => void;
	CanvasComponent: React.ComponentType<ReactSketchCanvasProps & {
		mode?: WIPModeType;
		index: number;
		isLoading?: boolean;
		statusCode?: string;
		isActivityOpen?: boolean;
		handleAddHistory: (index: number) => void;
	}>;
	// Work sample review props
	selectedPages?: number[];
	handleAddToSample: (index: number) => void;
	isPreparing?: boolean;
	isFinalGrade?: boolean;
	isWorkSampleReview?: boolean;	// Activity completion props
	currentActivity?: currentActivityAssessment;
	saveDrawing: (submit: boolean) => void;
	skipActivity?: (courseActivityId: number) => void;
	setIsDirty?: (isDirty: boolean) => void;
	featureFlagSymbolSelector?: boolean;
}

const PDFViewport: React.FC<PDFViewportProps> = ({
	pdfData,
	numPages,
	onDocumentLoadSuccess,
	options,
	pageNumberArray,
	pageWidth,
	linkState,
	handleInternalLinkClick,
	hideLinkTooltip,
	onPageRenderSuccess,
	handleInternalLinkElement,
	isActivityOpen,
	size,
	colour,
	eraserSize,
	addHistory,
	CanvasComponent,
	selectedPages,
	handleAddToSample,
	isPreparing,
	isFinalGrade,
	isWorkSampleReview,	currentActivity,
	saveDrawing,
	skipActivity,
	setIsDirty,
	featureFlagSymbolSelector
}) => {
	// Get PDF Reader state from context
	const { documentLoaded } = usePDFReaderContext();
	const { t } = useTranslation();
	const { control } = useFormContext<WIPForm>();

	return (
		<div
			id="pdf-container"
			style={{ pageBreakAfter: 'always' }}
			className={classNames('flex-column flex-grow-1 overflow-y-auto')}
			role="presentation"
			onClick={(e) => {
				// Check if we clicked on an internal link
				if (e.target instanceof HTMLAnchorElement) {
					const anchor = e.target as HTMLAnchorElement;
					if (handleInternalLinkElement(anchor)) {
						e.preventDefault();
						e.stopPropagation();
					}
				}
			}}
		>
			{pdfData !== null ? (
				<Document
					file={pdfData}
					onLoadSuccess={onDocumentLoadSuccess}
					options={options}
					externalLinkTarget="_blank"
				>
					{Array.from(new Array(numPages), (_el, index) => (
						pageNumberArray && pageNumberArray.includes(index + 1) && (
							<React.Fragment key={`page_${index + 1}`}>
								<div style={{ position: 'relative' }} className="mb-2">
									{selectedPages && !selectedPages.includes(index + 1) && (
										<div id="divoverlay" className="workSampleOverlay"></div>
									)}
									{isWorkSampleReview && (
										<div className="page-button-container">
											<PageButton
												isPreparing={isPreparing}
												selectedPages={selectedPages}
												handleAddToSample={handleAddToSample}
												index={index}
												isFinalGrade={isFinalGrade}
											/>
										</div>
									)}
									<Controller
										name={`studentLayer.${index}`}
										control={control}
										key={`studentlayer_${index}`}
										render={({ field }) => (
											<PDFPageInteractive
												pageIndex={index}
												pageWidth={pageWidth}
												field={field}
												pageNumberArray={pageNumberArray}
												linkState={linkState}
												handleInternalLinkClick={handleInternalLinkClick}
												hideLinkTooltip={hideLinkTooltip}
												onPageRenderSuccess={onPageRenderSuccess}
												statusCode={currentActivity?.item?.activityStatus?.statusCode}
												isActivityOpen={isActivityOpen}
												size={size}
												colour={colour}
												eraserSize={eraserSize}
												addHistory={addHistory}
												CanvasComponent={CanvasComponent}
												setIsDirty={setIsDirty}
												featureFlagSymbolSelector={featureFlagSymbolSelector}
											/>
										)}
									/>
								</div>
							</React.Fragment>
						)
					))}
				</Document>
			) : (
				<FourOhFour />
			)}

			{/* Activity Completion Section */}
			{documentLoaded && currentActivity && isActivityOpen && isStudentApp && (
				<div className="p-3">
					<h5 className="pb-2">
						{currentActivity?.type === 'Activity' ? t('wip.is_activity_complete') : t('wip.is_your_assessment_complete')}
					</h5>
					<div className="d-flex justify-content-between flex-fill">
						<div className="d-flex align-items-center w-100">
							{currentActivity?.type === 'Activity' && (
								<Button
									className="btn-outline-primary me-2"
									onClick={() => currentActivity && skipActivity && skipActivity(currentActivity?.item?.id)}
								>
									{t('wip.skip')}
								</Button>
							)}
							<Button className="btn-primary" onClick={() => saveDrawing(true)}>
								{currentActivity?.type === 'Activity' ? t('wip.submit_next_activty') : t('yes')}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PDFViewport;
